/**
 * Dense virtualized volunteers table (TanStack Virtual + Query) — CRM redesign.
 * Div-grid layout (header + rows share one grid-template), 35px rows, pills, ＋ tile.
 * Reads filters[]/page/pageSize/sort from the shared filters store; server-side
 * filtering, pagination and sorting. Row checkboxes / ＋ feed the Selection store.
 * Source of truth: design_handoff_crm_redesign/README.md + mockup_2.dc.html.
 */

import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";

import type { Volunteer } from "@/api/client";
import { useVolunteers, useCount } from "@/api/hooks";
import { useFilters } from "@/store/filters";
import { useSelection, useIsSelected } from "@/store/selection";
import { Pill } from "@/components/ui/Pill";
import { READINESS_TONE, LOYALTY_TONE } from "@/lib/tones";
import { Spinner } from "@/components/ui/spinner";
import { useColumns, gridTemplate, visibleColumns, type ColumnKey } from "@/store/columns";

function cellText(value: unknown): string {
  if (value == null || value === "") return "—";
  return String(value);
}

/** membership may arrive as string | string[] via the loose index signature. */
function membershipText(v: Volunteer): string {
  const m = (v as Record<string, unknown>).membership;
  if (Array.isArray(m)) return m.length ? m.join(", ") : "—";
  return cellText(m);
}

export function VolunteersTable() {
  const filters = useFilters((s) => s.filters);
  const page = useFilters((s) => s.page);
  const pageSize = useFilters((s) => s.pageSize);
  const sort = useFilters((s) => s.sort);
  const setPage = useFilters((s) => s.setPage);

  const visibility = useColumns((s) => s.visibility);
  const template = gridTemplate(visibility);
  const cols = visibleColumns(visibility);

  const query = useVolunteers({ filters, page, pageSize, sort });
  // mob.er.ru omits `count` on the unfiltered full list; the dedicated count
  // endpoint always returns a total, so pagination works across all ~197k rows.
  const countQuery = useCount(filters);

  const total = countQuery.data?.count ?? query.data?.count ?? 0;
  const rows = React.useMemo(() => query.data?.results ?? [], [query.data]);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 35,
    overscan: 12,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Reset the scroll to the top whenever the page, filters or sort change — otherwise
  // the virtualized body keeps the previous page's scroll offset and the user lands
  // in the middle of a fresh page (F53).
  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    rowVirtualizer.scrollToOffset(0);
    // rowVirtualizer identity is stable; re-run only on view changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, sort]);

  return (
    <div className="flex h-full flex-col" data-testid="volunteers-table">
      {/* Sticky grid header */}
      <div
        className="sticky top-0 z-10 grid flex-shrink-0 items-center uppercase"
        style={{
          gridTemplateColumns: template,
          padding: "0 18px",
          height: 32,
          background: "var(--header-row, #FAFCFE)",
          borderBottom: "1px solid var(--border-hex)",
          fontSize: "10.5px",
          fontWeight: 600,
          letterSpacing: ".05em",
          color: "var(--text-muted, #94A3B8)",
        }}
      >
        <span>
          <SelectAllHeader rows={rows} />
        </span>
        {cols.map((col) => (
          <span key={col.key}>{col.label}</span>
        ))}
      </div>

      {/* Scrollable virtualized body */}
      <div ref={scrollRef} className="relative flex-1 overflow-auto">
        {query.isPending ? (
          <SkeletonRows template={template} cellCount={cols.length} />
        ) : query.isError ? (
          <div className="p-6 text-center text-sm text-destructive">
            Не удалось загрузить список.{" "}
            {query.error instanceof Error ? query.error.message : ""}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Ничего не найдено по текущему фильтру.
          </div>
        ) : (
          <div style={{ height: totalSize, position: "relative", width: "100%" }}>
            {virtualRows.map((vRow) => {
              const v = rows[vRow.index];
              return (
                <div
                  key={v.id}
                  data-index={vRow.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${vRow.start}px)`,
                  }}
                >
                  <Row volunteer={v} template={template} visibility={visibility} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination footer (step 6) */}
      <Pagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        total={total}
        loaded={rows.length}
        busy={query.isFetching || countQuery.isFetching}
        onSetPage={setPage}
      />
    </div>
  );
}

/** Build a compact page-number window around the current page. */
function pageWindow(page: number, pageCount: number): number[] {
  const span = 5;
  let start = Math.max(1, page - Math.floor(span / 2));
  const end = Math.min(pageCount, start + span - 1);
  start = Math.max(1, end - span + 1);
  const out: number[] = [];
  for (let p = start; p <= end; p++) out.push(p);
  return out;
}

interface PaginationProps {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  loaded: number;
  busy: boolean;
  onSetPage: (page: number) => void;
}

/** Bottom pagination bar (var(--panel), border-top var(--border-hex)): "Показаны from–to из total" + page buttons. */
function Pagination({
  page,
  pageCount,
  pageSize,
  total,
  loaded,
  busy,
  onSetPage,
}: PaginationProps) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  // While a new page is loading, `loaded` still reflects the PREVIOUS page (kept by
  // react-query placeholderData), so deriving `to` from it prints a wrong range.
  // Show the page's theoretical upper bound during the load and the exact count once
  // it settles (F53).
  const to =
    total === 0
      ? 0
      : busy
        ? Math.min(page * pageSize, total)
        : (page - 1) * pageSize + loaded;
  const pages = pageWindow(page, pageCount);

  const navBtn: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 26,
    minWidth: 26,
    padding: "4px 8px",
    borderRadius: 7,
    border: "1px solid var(--border-hex)",
    background: "var(--panel)",
    color: "var(--text-2)",
    cursor: "pointer",
    lineHeight: 1,
  };

  return (
    <div
      className="flex flex-shrink-0 items-center justify-between"
      style={{
        background: "var(--panel, #fff)",
        borderTop: "1px solid var(--border-hex)",
        padding: "8px 18px",
        fontSize: 12,
        color: "var(--text-3)",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span>
          Показаны{" "}
          <span className="font-mono tabular-nums" style={{ color: "var(--text-1)" }}>
            {from.toLocaleString("ru-RU")}–{to.toLocaleString("ru-RU")}
          </span>{" "}
          из{" "}
          <span className="font-mono tabular-nums" style={{ color: "var(--text-1)" }}>
            {total.toLocaleString("ru-RU")}
          </span>
        </span>
        {busy ? <Spinner className="text-muted-foreground" /> : null}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button
          type="button"
          aria-label="Назад"
          onClick={() => onSetPage(page - 1)}
          disabled={page <= 1}
          style={{
            ...navBtn,
            color: page <= 1 ? "var(--control-border)" : "var(--text-2)",
            cursor: page <= 1 ? "default" : "pointer",
          }}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {pages.map((p) => {
          const active = p === page;
          return (
            <button
              key={p}
              type="button"
              onClick={() => onSetPage(p)}
              className="font-mono tabular-nums"
              style={{
                ...navBtn,
                border: active ? "1px solid var(--accent-hex)" : "1px solid var(--border-hex)",
                background: active ? "var(--accent-weak)" : "var(--panel)",
                color: active ? "var(--accent-weak-text)" : "var(--text-2)",
                fontWeight: active ? 600 : 400,
                padding: "4px 10px",
              }}
            >
              {p}
            </button>
          );
        })}

        <button
          type="button"
          aria-label="Вперёд"
          onClick={() => onSetPage(page + 1)}
          disabled={page >= pageCount}
          style={{
            ...navBtn,
            color: page >= pageCount ? "var(--control-border)" : "var(--text-2)",
            cursor: page >= pageCount ? "default" : "pointer",
          }}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Single dense data row (35px) — div-grid mirroring the header template. */
function Row({
  volunteer: v,
  template,
  visibility,
}: {
  volunteer: Volunteer;
  template: string;
  visibility: Record<ColumnKey, boolean>;
}) {
  const selected = useIsSelected(v.id);
  const toggleId = useSelection((s) => s.toggleId);
  const navigate = useNavigate();

  const fio = [v.last_name, v.first_name].filter(Boolean).join(" ");
  const readinessTone = v.readiness ? READINESS_TONE[v.readiness] : undefined;
  const loyaltyTone = v.loyalty ? LOYALTY_TONE[v.loyalty] : undefined;

  return (
    <div
      className="grid cursor-pointer items-center"
      style={{
        gridTemplateColumns: template,
        padding: "0 18px",
        height: 35,
        borderBottom: "1px solid var(--border-soft, #EEF2F7)",
        fontSize: "12.5px",
        position: "relative",
        background: selected ? "rgba(37,99,235,.05)" : undefined,
      }}
      onClick={() => navigate(`/activists/${v.id}`, { state: { volunteer: v } })}
    >
      {/* selected left bar */}
      {selected && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            top: 5,
            bottom: 5,
            width: "2.5px",
            borderRadius: 2,
            background: "var(--accent-hex, #2563EB)",
          }}
        />
      )}

      {/* [0] row checkbox 15px r4 */}
      <span>
        <RowCheck
          selected={selected}
          onToggle={(e) => {
            e.stopPropagation();
            toggleId(v.id);
          }}
        />
      </span>

      {/* [1] Активист (pinned — always visible) */}
      {visibility.activist && (
        <span
          style={{
            minWidth: 0,
            display: "flex",
            alignItems: "baseline",
            gap: 7,
            overflow: "hidden",
          }}
        >
          <b
            style={{
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: "var(--text-strong)",
            }}
          >
            {fio || "—"}
          </b>
          {v.patronymic ? (
            <span
              style={{
                color: "var(--text-muted, #94A3B8)",
                fontSize: "11.5px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {v.patronymic}
            </span>
          ) : null}
        </span>
      )}

      {/* [2] Отделение */}
      {visibility.branch && (
        <span
          style={{
            color: "var(--text-2, #475569)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {cellText(v.branch_name)}
        </span>
      )}

      {/* [3] Телефон mono */}
      {visibility.phone && (
        <span
          className="font-mono"
          style={{ fontSize: "11.5px", color: "var(--text-3, #64748B)" }}
        >
          {cellText(v.phone_mobile)}
        </span>
      )}

      {/* [4] Д.Р. mono */}
      {visibility.birthday && (
        <span
          className="font-mono"
          style={{ fontSize: "11.5px", color: "var(--text-muted, #94A3B8)" }}
        >
          {cellText(v.birthday)}
        </span>
      )}

      {/* [5] Готовность — pill + dot */}
      {visibility.readiness && (
        <span>
          {readinessTone ? (
            <Pill tone={readinessTone} withDot>
              {v.readiness}
            </Pill>
          ) : (
            <span style={{ color: "var(--text-muted, #94A3B8)" }}>—</span>
          )}
        </span>
      )}

      {/* [6] Лояльность — pill no dot */}
      {visibility.loyalty && (
        <span>
          {loyaltyTone ? (
            <Pill tone={loyaltyTone}>{v.loyalty}</Pill>
          ) : (
            <span style={{ color: "var(--text-muted, #94A3B8)" }}>—</span>
          )}
        </span>
      )}

      {/* [7] Членство */}
      {visibility.membership && (
        <span
          style={{
            color: "var(--text-3, #64748B)",
            fontSize: "11.5px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {membershipText(v)}
        </span>
      )}
    </div>
  );
}

/** 15px / r4 row checkbox (selected: blue with white ✓). */
function RowCheck({
  selected,
  onToggle,
}: {
  selected: boolean;
  onToggle: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      aria-label="Выбрать строку"
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 15,
        height: 15,
        borderRadius: 4,
        border: selected ? "none" : "1.5px solid var(--control-border)",
        background: selected ? "var(--accent-hex, #2563EB)" : "var(--panel)",
        color: "var(--on-accent)",
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        padding: 0,
      }}
    >
      {selected ? "✓" : ""}
    </button>
  );
}

/** Skeleton placeholder rows preserving the 35px grid rhythm. */
function SkeletonRows({ template, cellCount }: { template: string; cellCount: number }) {
  return (
    <div>
      {Array.from({ length: 16 }).map((_, i) => (
        <div
          key={`skeleton-${i}`}
          className="grid items-center"
          style={{
            gridTemplateColumns: template,
            padding: "0 18px",
            height: 35,
            borderBottom: "1px solid var(--border-soft, #EEF2F7)",
          }}
        >
          <span
            style={{
              width: 15,
              height: 15,
              borderRadius: 4,
              border: "1.5px solid var(--border-hex)",
              background: "var(--panel)",
            }}
          />
          {Array.from({ length: cellCount }).map((__, ci) => (
            <span
              key={ci}
              className="animate-pulse"
              style={{
                height: 10,
                borderRadius: 4,
                background: "var(--border-soft)",
                marginRight: 18,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Header checkbox: toggles all rows on the current page in/out of the id selection. */
function SelectAllHeader({ rows }: { rows: Volunteer[] }) {
  const mode = useSelection((s) => s.mode);
  const ids = useSelection((s) => s.ids);
  const selectIds = useSelection((s) => s.selectIds);

  const pageIds = React.useMemo(() => rows.map((r) => r.id), [rows]);
  const selectedSet = React.useMemo(
    () => (mode === "ids" && ids ? new Set(ids) : new Set<number>()),
    [mode, ids],
  );

  const selectedOnPage = pageIds.filter((id) => selectedSet.has(id)).length;
  const allChecked = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const someChecked = selectedOnPage > 0 && !allChecked;

  function toggleAll() {
    if (allChecked) {
      const remaining = Array.from(selectedSet).filter((id) => !pageIds.includes(id));
      selectIds(remaining);
    } else {
      const next = new Set(selectedSet);
      for (const id of pageIds) next.add(id);
      selectIds(Array.from(next));
    }
  }

  return (
    <button
      type="button"
      aria-label="Выбрать все на странице"
      disabled={pageIds.length === 0}
      onClick={toggleAll}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 15,
        height: 15,
        borderRadius: 4,
        border: allChecked ? "none" : "1.5px solid var(--control-border)",
        background: allChecked
          ? "var(--accent-hex, #2563EB)"
          : someChecked
            ? "var(--accent-weak, #EFF4FE)"
            : "var(--panel)",
        // "–" on the light-blue indeterminate background must be the accent colour,
        // not white-on-white (F24). The full-check "✓" sits on solid accent → on-accent.
        color: someChecked ? "var(--accent-hex, #2563EB)" : "var(--on-accent)",
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: pageIds.length === 0 ? "default" : "pointer",
        padding: 0,
        opacity: pageIds.length === 0 ? 0.5 : 1,
      }}
    >
      {allChecked ? "✓" : someChecked ? "–" : ""}
    </button>
  );
}
